/**
 * fillBucket: scanline flood-fill for the canvas.
 *
 * M2.T7 per IMPLEMENTATION_PLAN §7 M2.T7 + PRD §6.1 + §5.9.
 *
 * **Free-Draw-only.** The component-level `freeDrawOnly` prop controls
 * whether the fill tool is rendered. The reducer independently enforces
 * `state.kind === "freeDraw"` in STROKE_FILL (§5.9).
 *
 * Algorithm: 4-connected scanline flood-fill (Cohen–Sutherland-style
 * horizontal span pushing). Tolerance: per-channel 4-channel colour
 * distance ≤ FILL_TOLERANCE (default 32) to handle anti-aliased edges.
 *
 * Operates on ImageData directly; avoids recursive stack overflow by using
 * an explicit queue/stack of pending spans.
 */

import type { BrandColor } from "@drawlol/shared";

/** Per-channel tolerance for flood-fill colour matching (0–255). */
export const FILL_TOLERANCE = 32;

/** Parse a hex color to [r, g, b, a]. Alpha defaults to 255. */
export function hexToRgba(hex: string): [number, number, number, number] {
  const n = hex.replace("#", "");
  if (n.length === 3) {
    return [
      parseInt(n[0]! + n[0]!, 16),
      parseInt(n[1]! + n[1]!, 16),
      parseInt(n[2]! + n[2]!, 16),
      255,
    ];
  }
  return [
    parseInt(n.slice(0, 2), 16),
    parseInt(n.slice(2, 4), 16),
    parseInt(n.slice(4, 6), 16),
    255,
  ];
}

/** Returns true if the pixel at (x,y) is within tolerance of the target colour. */
function pixelMatchesTarget(
  data: Uint8ClampedArray,
  idx: number,
  target: [number, number, number, number],
  tolerance: number,
): boolean {
  return (
    Math.abs(data[idx]! - target[0]) <= tolerance &&
    Math.abs(data[idx + 1]! - target[1]) <= tolerance &&
    Math.abs(data[idx + 2]! - target[2]) <= tolerance &&
    Math.abs(data[idx + 3]! - target[3]) <= tolerance
  );
}

/** Writes a fill color to the pixel at index `idx`. */
function setPixel(
  data: Uint8ClampedArray,
  idx: number,
  fill: [number, number, number, number],
): void {
  data[idx] = fill[0];
  data[idx + 1] = fill[1];
  data[idx + 2] = fill[2];
  data[idx + 3] = fill[3];
}

/**
 * Performs a scanline flood-fill on the given ImageData, starting at the
 * physical point (startX, startY). Mutates `imageData` in place.
 *
 * Note: coordinates are in canvas *physical* pixels (backing-store pixels).
 * The caller (`applyFill`) converts from CSS pixels to physical via DPR
 * before calling.
 *
 * @param imageData   Raw ImageData (physical pixels)
 * @param startX      Physical X coordinate of the fill origin
 * @param startY      Physical Y coordinate of the fill origin
 * @param fillColor   Hex colour string to fill with
 * @param tolerance   Per-channel match tolerance (default FILL_TOLERANCE)
 */
export function floodFill(
  imageData: ImageData,
  startX: number,
  startY: number,
  fillColor: string,
  tolerance: number = FILL_TOLERANCE,
): void {
  const { width, height, data } = imageData;

  startX = Math.round(startX);
  startY = Math.round(startY);

  if (startX < 0 || startX >= width || startY < 0 || startY >= height) return;

  const startIdx = (startY * width + startX) * 4;
  const target: [number, number, number, number] = [
    data[startIdx]!,
    data[startIdx + 1]!,
    data[startIdx + 2]!,
    data[startIdx + 3]!,
  ];
  const fill = hexToRgba(fillColor);

  // If start pixel is already fill color (within tolerance), nothing to do.
  if (pixelMatchesTarget(data, startIdx, fill, 1)) return;

  // Stack-based scanline fill: avoids recursion depth issues.
  // Each entry is [x, y] of a seed pixel.
  const stack: [number, number][] = [[startX, startY]];
  // visited bitmap to avoid re-processing
  const visited = new Uint8Array(width * height);

  while (stack.length > 0) {
    const entry = stack.pop()!;
    let [x] = entry;
    const [, y] = entry;

    // Walk left to find the leftmost pixel in this horizontal span.
    while (
      x > 0 &&
      !visited[y * width + (x - 1)] &&
      pixelMatchesTarget(data, (y * width + (x - 1)) * 4, target, tolerance)
    ) {
      x--;
    }

    let spanAbove = false;
    let spanBelow = false;

    // Walk right filling the span.
    while (x < width) {
      const idx = (y * width + x) * 4;
      if (visited[y * width + x] || !pixelMatchesTarget(data, idx, target, tolerance)) break;

      setPixel(data, idx, fill);
      visited[y * width + x] = 1;

      // Check above
      if (y > 0) {
        const aboveIdx = ((y - 1) * width + x) * 4;
        if (
          !spanAbove &&
          !visited[(y - 1) * width + x] &&
          pixelMatchesTarget(data, aboveIdx, target, tolerance)
        ) {
          stack.push([x, y - 1]);
          spanAbove = true;
        } else if (
          spanAbove &&
          (visited[(y - 1) * width + x] || !pixelMatchesTarget(data, aboveIdx, target, tolerance))
        ) {
          spanAbove = false;
        }
      }

      // Check below
      if (y < height - 1) {
        const belowIdx = ((y + 1) * width + x) * 4;
        if (
          !spanBelow &&
          !visited[(y + 1) * width + x] &&
          pixelMatchesTarget(data, belowIdx, target, tolerance)
        ) {
          stack.push([x, y + 1]);
          spanBelow = true;
        } else if (
          spanBelow &&
          (visited[(y + 1) * width + x] || !pixelMatchesTarget(data, belowIdx, target, tolerance))
        ) {
          spanBelow = false;
        }
      }

      x++;
    }
  }
}

/**
 * Apply a flood-fill to a canvas at a CSS-pixel point (cssX, cssY).
 * Converts to physical backing-store pixels via the canvas DPR, runs the
 * fill, and puts the result back.
 *
 * M2.fix(coords): coordinates are now canvas-relative CSS pixels, the same
 * space pointer events deliver clicks in, so the fill always lands at the
 * click point regardless of viewport size.
 *
 * @param ctx    Canvas 2D context
 * @param cssX   Canvas-relative CSS X (0..rect.width)
 * @param cssY   Canvas-relative CSS Y (0..rect.height)
 * @param color  BrandColor hex
 * @param dpr    devicePixelRatio used when the canvas was sized (defaults
 *               to `window.devicePixelRatio || 1`; tests can pin it for
 *               deterministic conversion)
 */
export function applyFill(
  ctx: CanvasRenderingContext2D,
  cssX: number,
  cssY: number,
  color: BrandColor,
  dpr: number = (typeof window !== "undefined" && window.devicePixelRatio) || 1,
): void {
  const physW = ctx.canvas.width;
  const physH = ctx.canvas.height;

  // CSS → physical: canvas.width === cssWidth * dpr (we maintain this in
  // Canvas.applySize), so the scale is exactly `dpr` on both axes.
  const physX = Math.round(cssX * dpr);
  const physY = Math.round(cssY * dpr);

  // Reset transform to read raw backing-store pixels
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  const imageData = ctx.getImageData(0, 0, physW, physH);
  floodFill(imageData, physX, physY, color);
  ctx.putImageData(imageData, 0, 0);

  ctx.restore();
}
